import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, Select } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_COMPANY, UPDATE_COMPANY } from 'src/graphql/companies.graphql'
import { GET_MY_COMPANIES } from 'src/graphql/users.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'

import FieldLabel from './FieldLabel'

interface DocumentIntelligenceCardProps {
  companyData: {
    companyId: string
    documentIntelligenceEnabled: boolean
  }
  initialOpened?: boolean
}

const SELECT_OPTIONS = [
  { value: 'yes', label: 'Enabled' },
  { value: 'no', label: 'Disabled' },
] as const

const DocumentIntelligenceCard = ({
  companyData,
  initialOpened = false,
}: DocumentIntelligenceCardProps) => {
  const [enabled, setEnabled] = useState(companyData.documentIntelligenceEnabled)
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)

  const [updateCompany, { loading }] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      toast('Document intelligence settings updated', 'success')
      setEditing(false)
    },
    onError: (error) => {
      toast(`Failed to update settings: ${error.message}`, 'error')
    },
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: { id: companyData.companyId },
      },
      { query: GET_MY_COMPANIES },
    ],
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    if (!editing) {
      setEnabled(companyData.documentIntelligenceEnabled)
    }
  }, [companyData, editing])

  const handleSave = async () => {
    await updateCompany({
      variables: {
        id: companyData.companyId,
        input: {
          documentIntelligenceEnabled: enabled,
        },
      },
    })
  }

  const handleCancel = () => {
    setEnabled(companyData.documentIntelligenceEnabled)
    setEditing(false)
  }

  const enableLabel = (
    <FieldLabel
      label="Enable Document Intelligence"
      tooltip="When enabled, Double Nickel uses AI to automatically classify documents and extract key fields such as expiration date and category."
    />
  )

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Document Intelligence
        {!editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              setEditing(true)
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {editing && (
          <div
            className="ml-auto flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <DNButton
              variant="transparent"
              onClick={handleSave}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={handleCancel}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>

      <Collapse in={opened} transitionDuration={400}>
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-3">
            {editing ? (
              <Select
                label={enableLabel}
                value={enabled ? 'yes' : 'no'}
                onChange={(val) => setEnabled(val === 'yes')}
                data={SELECT_OPTIONS}
                classNames={{
                  label: 'text-sm font-medium text-doubleNickel-gray-700',
                  input: 'text-sm',
                }}
              />
            ) : (
              <StyledReadOnlyInput
                label={enableLabel}
                value={enabled ? 'Enabled' : 'Disabled'}
              />
            )}
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default DocumentIntelligenceCard
